import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AccountBalanceWalletOutlined,
  AttachMoney,
  CheckCircleOutline,
  Close,
  EmailOutlined,
  LocalOfferOutlined,
  PaidOutlined,
  PendingActions,
  ReceiptLongOutlined,
  Refresh,
  Search,
  SellOutlined,
  SettingsOutlined,
  StorefrontOutlined,
  WarningAmber,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import AdminNewSidebar from "components/admin/AdminNewSidebar";
import { Header } from "components";
import apis from "services";

const EMPTY_ANALYTICS = {
  totalCompletedSalesCount: 0,
  totalCompletedSalesValue: 0,
  activeListingsCount: 0,
  soldListingsCount: 0,
  offersByStatus: {},
  sellerPayments: {
    Pending: { count: 0, amount: 0 },
    Paid: { count: 0, amount: 0 },
    Cancelled: { count: 0, amount: 0 },
  },
  totalMarketplaceFeesCollected: 0,
  totalAmountAwaitingSellerPayment: 0,
};

const EMPTY_FILTERS = {
  search: "",
  status: "",
  seller: "",
  buyer: "",
  listing: "",
  saleDateFrom: "",
  saleDateTo: "",
  paymentDateFrom: "",
  paymentDateTo: "",
  transactionReference: "",
  paymentReference: "",
};

const cardSx = {
  height: "100%",
  borderRadius: 3,
  border: "1px solid rgba(20, 20, 20, 0.09)",
  boxShadow: "0 10px 28px rgba(20, 20, 20, 0.055)",
};

const filterFieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 46,
    borderRadius: 2.25,
    bgcolor: "#fafafa",
    transition: "background-color .18s ease, box-shadow .18s ease",
    "& fieldset": { borderColor: "#dedede" },
    "&:hover": { bgcolor: "#fff", "& fieldset": { borderColor: "#a9a9a9" } },
    "&.Mui-focused": {
      bgcolor: "#fff",
      boxShadow: "0 0 0 3px rgba(230, 14, 16, .09)",
      "& fieldset": { borderColor: "#E60E10" },
    },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#c90c0e" },
  "& input::placeholder": { color: "#8a8a8a", opacity: 1 },
};

const primaryButtonSx = {
  background: "linear-gradient(135deg, #E60E10 0%, #ff5f45 100%)",
  boxShadow: "0 8px 20px rgba(230, 14, 16, 0.2)",
  "&:hover": { background: "linear-gradient(135deg, #c90c0e 0%, #f24e34 100%)" },
};

const currency = (value, code = "MXN") =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: code, minimumFractionDigits: 2 }).format(
    Number(value || 0)
  );

const dateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
};

const person = (value) => value?.name || value?.nickName || value?.email || "—";
const shortId = (value) => (value ? `…${String(value).slice(-8)}` : "—");

const statusColor = { Pending: "warning", Paid: "success", Cancelled: "default" };

function StatusChip({ status, overdue }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Chip label={status} size="small" color={statusColor[status] || "default"} variant={status === "Cancelled" ? "outlined" : "filled"} />
      {overdue ? (
        <Tooltip title="Suggested payment date has passed. Payment remains a manual admin action.">
          <Chip icon={<WarningAmber />} label="Past suggested date" size="small" color="error" variant="outlined" />
        </Tooltip>
      ) : null}
    </Stack>
  );
}

function EmailStatusChip({ payment }) {
  const sent = Boolean(payment?.paymentEmailSent);
  const failed = !sent && Number(payment?.paymentEmailAttempts || 0) > 0 && Boolean(payment?.paymentEmailError);
  return (
    <Chip
      size="small"
      icon={<EmailOutlined />}
      label={sent ? "Yes" : failed ? "Failed" : "No"}
      color={sent ? "success" : failed ? "error" : "default"}
      variant={sent ? "filled" : "outlined"}
      sx={{ fontWeight: 750 }}
    />
  );
}

function StatCard({ title, value, helper, icon, urgent }) {
  return (
    <Card sx={{ ...cardSx, borderColor: urgent ? "rgba(230, 14, 16, .28)" : cardSx.border }}>
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 700 }}>{title}</Typography>
            <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.75, color: urgent ? "#c90c0e" : "#171717" }}>{value}</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>{helper}</Typography>
          </Box>
          <Box sx={{ width: 42, height: 42, borderRadius: 2.5, display: "grid", placeItems: "center", color: urgent ? "#E60E10" : "#292929", bgcolor: urgent ? "rgba(230,14,16,.08)" : "#f2f2f2" }}>{icon}</Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function Info({ label, value, accent }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 750, color: accent ? "#E60E10" : "text.primary", wordBreak: "break-word" }}>{value ?? "—"}</Typography>
    </Box>
  );
}

const MarketplaceManagement = () => {
  const { adminUser } = useSelector((store) => store.admin);
  const canManage = adminUser?.type === "owner";
  const [tab, setTab] = useState(0);
  const [range, setRange] = useState({ from: "", to: "" });
  const [analytics, setAnalytics] = useState(EMPTY_ANALYTICS);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState("");

  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [payments, setPayments] = useState({ items: [], total: 0, totalPages: 0 });
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsError, setPaymentsError] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("saleDate:desc");

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [action, setAction] = useState(null);
  const [actionForm, setActionForm] = useState({ actualPaymentDate: "", paymentReference: "", adminNotes: "" });
  const [cancelConfirmed, setCancelConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [retryConfirm, setRetryConfirm] = useState(false);
  const [retryingEmail, setRetryingEmail] = useState(false);

  const [fee, setFee] = useState(null);
  const [feeHistory, setFeeHistory] = useState([]);
  const [feeLoading, setFeeLoading] = useState(false);
  const [feeDialog, setFeeDialog] = useState(false);
  const [feeForm, setFeeForm] = useState({ ratePercent: "", reason: "" });
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    setAnalyticsError("");
    try {
      const response = await apis.getMarketplaceAnalytics(range);
      setAnalytics(response?.data?.data || EMPTY_ANALYTICS);
    } catch (error) {
      setAnalyticsError(error.message || "Unable to load marketplace analytics.");
    } finally {
      setAnalyticsLoading(false);
    }
  }, [range]);

  const fetchPayments = useCallback(async () => {
    setPaymentsLoading(true);
    setPaymentsError("");
    try {
      const [sortBy, sortOrder] = sort.split(":");
      const cleanFilters = Object.fromEntries(Object.entries(filters).filter(([, value]) => value));
      const response = await apis.getSellerPayments({ ...cleanFilters, page, limit: 20, sortBy, sortOrder });
      setPayments(response?.data?.data || { items: [], total: 0, totalPages: 0 });
    } catch (error) {
      setPaymentsError(error.message || "Unable to load seller payments.");
    } finally {
      setPaymentsLoading(false);
    }
  }, [filters, page, sort]);

  const fetchFee = useCallback(async () => {
    setFeeLoading(true);
    try {
      const [feeResponse, historyResponse] = await Promise.all([
        apis.getMarketplaceFee(),
        apis.getMarketplaceFeeHistory({ limit: 50 }),
      ]);
      setFee(feeResponse?.data?.data || null);
      setFeeHistory(historyResponse?.data?.data?.items || []);
    } catch (error) {
      toast.error(error.message || "Unable to load fee settings.");
    } finally {
      setFeeLoading(false);
    }
  }, []);

  const fetchAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const response = await apis.getMarketplaceAudit({ limit: 100 });
      setAudit(response?.data?.data?.items || []);
    } catch (error) {
      toast.error(error.message || "Unable to load marketplace audit history.");
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);
  useEffect(() => { if (tab === 1) fetchPayments(); }, [tab, fetchPayments]);
  useEffect(() => { if (tab === 2) fetchFee(); }, [tab, fetchFee]);
  useEffect(() => { if (tab === 3) fetchAudit(); }, [tab, fetchAudit]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ _id: id });
    try {
      const response = await apis.getSellerPayment(id);
      setDetail(response?.data?.data || null);
    } catch (error) {
      toast.error(error.message || "Unable to load seller-payment details.");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async () => {
    if (!detail?._id) return;
    const response = await apis.getSellerPayment(detail._id);
    setDetail(response?.data?.data || null);
  };

  const beginAction = (nextAction) => {
    const localNow = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setAction(nextAction);
    setActionForm({
      actualPaymentDate: nextAction === "Paid" ? localNow : "",
      paymentReference: nextAction === "Paid" ? detail?.paymentReference || "" : "",
      adminNotes: detail?.adminNotes || "",
    });
    setCancelConfirmed(false);
  };

  const submitPaymentAction = async () => {
    if (!detail) return;
    if (action === "Paid" && (!actionForm.actualPaymentDate || !actionForm.paymentReference.trim())) {
      toast.error("Payment date and payment reference are required.");
      return;
    }
    if (action === "Cancelled" && (!cancelConfirmed || !actionForm.adminNotes.trim())) {
      toast.error("Confirm cancellation and provide a reason.");
      return;
    }
    setSaving(true);
    try {
      const body = { version: detail.__v, adminNotes: actionForm.adminNotes.trim() };
      if (action !== "notes") body.status = action;
      if (action === "Paid") {
        body.actualPaymentDate = new Date(actionForm.actualPaymentDate).toISOString();
        body.paymentReference = actionForm.paymentReference.trim();
      }
      if (action === "Cancelled") body.cancellationReason = actionForm.adminNotes.trim();
      const response = await apis.updateSellerPayment(detail._id, body);
      toast.success(action === "notes" ? "Admin notes updated." : `Seller payment marked ${action}.`);
      if (response?.data?.warning) toast.warning(response.data.warning);
      setAction(null);
      await Promise.all([refreshDetail(), fetchPayments(), fetchAnalytics()]);
    } catch (error) {
      toast.error(error.message || "Unable to update seller payment.");
    } finally {
      setSaving(false);
    }
  };

  const retryPaymentEmail = async () => {
    if (!detail?._id) return;
    setRetryingEmail(true);
    try {
      const response = await apis.retrySellerPaymentEmail(detail._id);
      if (response?.data?.emailDelivery?.state === "sent") {
        toast.success("Payment confirmation email sent successfully.");
      } else {
        toast.error(response?.data?.warning || "The email could not be delivered. The payment remains Paid.");
      }
      setRetryConfirm(false);
      await Promise.all([refreshDetail(), fetchPayments(), fetchAudit()]);
    } catch (error) {
      toast.error(error.message || "Unable to retry the payment email.");
    } finally {
      setRetryingEmail(false);
    }
  };

  const submitFee = async () => {
    const rate = Number(feeForm.ratePercent);
    if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
      toast.error("Enter a percentage between 0 and 100.");
      return;
    }
    setSaving(true);
    try {
      await apis.updateMarketplaceFee({ ratePercent: rate, reason: feeForm.reason.trim() });
      toast.success("Marketplace fee updated for future transactions.");
      setFeeDialog(false);
      await Promise.all([fetchFee(), fetchAnalytics(), fetchAudit()]);
    } catch (error) {
      toast.error(error.message || "Unable to update marketplace fee.");
    } finally {
      setSaving(false);
    }
  };

  const statCards = useMemo(() => [
    ["Completed sales", Number(analytics.totalCompletedSalesCount).toLocaleString(), currency(analytics.totalCompletedSalesValue), <SellOutlined />],
    ["Active listings", Number(analytics.activeListingsCount).toLocaleString(), "Currently available", <StorefrontOutlined />],
    ["Sold listings", Number(analytics.soldListingsCount).toLocaleString(), "Fully allocated", <CheckCircleOutline />],
    ["Pending seller payments", Number(analytics.sellerPayments?.Pending?.count || 0).toLocaleString(), currency(analytics.sellerPayments?.Pending?.amount), <PendingActions />, true],
    ["Paid seller payments", Number(analytics.sellerPayments?.Paid?.count || 0).toLocaleString(), currency(analytics.sellerPayments?.Paid?.amount), <PaidOutlined />],
    ["Cancelled payments", Number(analytics.sellerPayments?.Cancelled?.count || 0).toLocaleString(), currency(analytics.sellerPayments?.Cancelled?.amount), <WarningAmber />],
    ["Marketplace fees", currency(analytics.totalMarketplaceFeesCollected), "Snapshotted on completed sales", <AttachMoney />],
    ["Awaiting seller payment", currency(analytics.totalAmountAwaitingSellerPayment), "Manual payment workload", <AccountBalanceWalletOutlined />, true],
  ], [analytics]);

  return (
    <Box
      className="dashboard-main"
      sx={{
        width: "100%",
        maxWidth: "100vw",
        height: "100vh",
        minWidth: 0,
        minHeight: 0,
        overflow: "hidden",
        "& > .sidebar": {
          flexShrink: 0,
          height: "100vh",
          minHeight: "100vh",
          backgroundColor: "#c70d22",
          backgroundSize: "cover",
          backgroundPosition: "center top",
        },
        "& > .sidebar::-webkit-scrollbar-track": {
          backgroundColor: "#c70d22",
        },
      }}
    >
      <AdminNewSidebar />
      <Box
        className="dashboard-section-main"
        sx={{
          flex: "1 1 0",
          width: "auto !important",
          minWidth: 0,
          height: "100vh",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          component="main"
          className="custom-scroll"
          sx={{
            width: "100%",
            height: "100%",
            minWidth: 0,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            bgcolor: "#f7f7f7",
          }}
        >
          <Header />
          <Container maxWidth={false} sx={{ width: "100%", minWidth: 0, py: { xs: 3, md: 4 }, px: { xs: 2, md: 3.5 } }} className="mainContainerComp">
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} spacing={2.5} sx={{ mb: 3.5 }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 900, color: "#171717" }}>Marketplace Management</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: .5 }}>Sales intelligence, seller settlements, fees, and immutable admin history.</Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField sx={filterFieldSx} size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={range.from} onChange={(event) => setRange((old) => ({ ...old, from: event.target.value }))} />
                <TextField sx={filterFieldSx} size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={range.to} onChange={(event) => setRange((old) => ({ ...old, to: event.target.value }))} />
                <Tooltip title="Refresh current data"><IconButton onClick={() => tab === 0 ? fetchAnalytics() : tab === 1 ? fetchPayments() : tab === 2 ? fetchFee() : fetchAudit()}><Refresh /></IconButton></Tooltip>
              </Stack>
            </Stack>

            {!canManage ? <Alert severity="warning" sx={{ mb: 2 }}>Your account can view this module but cannot change fee or seller-payment records.</Alert> : null}

            <Card sx={{ ...cardSx, mb: 3 }}>
              <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto" sx={{ px: 1, "& .Mui-selected": { color: "#E60E10 !important" }, "& .MuiTabs-indicator": { bgcolor: "#E60E10" } }}>
                <Tab label="Overview" />
                <Tab label="Seller Payments" />
                <Tab label="Fee Settings" />
                <Tab label="Audit History" />
              </Tabs>
            </Card>

            {tab === 0 ? (
              <>
                {analyticsError ? <Alert severity="error" action={<Button onClick={fetchAnalytics}>Retry</Button>} sx={{ mb: 2 }}>{analyticsError}</Alert> : null}
                <Grid container spacing={2}>
                  {statCards.map(([title, value, helper, icon, urgent]) => (
                    <Grid item xs={12} sm={6} lg={3} key={title}>{analyticsLoading ? <Skeleton variant="rounded" height={132} /> : <StatCard title={title} value={value} helper={helper} icon={icon} urgent={urgent} />}</Grid>
                  ))}
                </Grid>
                <Card sx={{ ...cardSx, mt: 2 }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Box><Typography variant="h6" sx={{ fontWeight: 850 }}>Offer activity</Typography><Typography variant="body2" color="text.secondary">Status distribution for the selected date range.</Typography></Box>
                      <LocalOfferOutlined sx={{ color: "#E60E10" }} />
                    </Stack>
                    <Stack direction="row" useFlexGap flexWrap="wrap" gap={1.25}>
                      {Object.keys(analytics.offersByStatus || {}).length ? Object.entries(analytics.offersByStatus).map(([status, count]) => <Chip key={status} label={`${status.replaceAll("_", " ")}: ${count}`} variant="outlined" sx={{ textTransform: "capitalize", fontWeight: 700 }} />) : <Typography variant="body2" color="text.secondary">No offers found for this range.</Typography>}
                    </Stack>
                  </CardContent>
                </Card>
              </>
            ) : null}

            {tab === 1 ? (
              <Stack spacing={2.5} sx={{ minWidth: 0 }}>
                <Card sx={cardSx}>
                  <CardContent sx={{ p: { xs: 2, md: 2.75 }, ...filterFieldSx }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 850, mb: .25 }}>Find seller payments</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.25 }}>Search transaction records or narrow the settlement queue.</Typography>
                    <Grid container spacing={1.75}>
                      <Grid item xs={12} md={4}><TextField fullWidth size="small" label="Search" placeholder="Name, email, ID or reference" InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20, color: "#6f6f6f" }} /></InputAdornment> }} value={draftFilters.search} onChange={(e) => setDraftFilters((old) => ({ ...old, search: e.target.value }))} /></Grid>
                      <Grid item xs={6} md={2}><TextField select fullWidth size="small" label="Status" value={draftFilters.status} onChange={(e) => setDraftFilters((old) => ({ ...old, status: e.target.value }))}><MenuItem value="">All</MenuItem>{["Pending", "Paid", "Cancelled"].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}</TextField></Grid>
                      <Grid item xs={6} md={2}><TextField fullWidth size="small" label="Seller" value={draftFilters.seller} onChange={(e) => setDraftFilters((old) => ({ ...old, seller: e.target.value }))} /></Grid>
                      <Grid item xs={6} md={2}><TextField fullWidth size="small" label="Buyer" value={draftFilters.buyer} onChange={(e) => setDraftFilters((old) => ({ ...old, buyer: e.target.value }))} /></Grid>
                      <Grid item xs={6} md={2}><TextField fullWidth size="small" label="Listing ID" value={draftFilters.listing} onChange={(e) => setDraftFilters((old) => ({ ...old, listing: e.target.value }))} /></Grid>
                      <Grid item xs={6} md={2}><TextField fullWidth size="small" type="date" label="Sale from" InputLabelProps={{ shrink: true }} value={draftFilters.saleDateFrom} onChange={(e) => setDraftFilters((old) => ({ ...old, saleDateFrom: e.target.value }))} /></Grid>
                      <Grid item xs={6} md={2}><TextField fullWidth size="small" type="date" label="Sale to" InputLabelProps={{ shrink: true }} value={draftFilters.saleDateTo} onChange={(e) => setDraftFilters((old) => ({ ...old, saleDateTo: e.target.value }))} /></Grid>
                      <Grid item xs={6} md={2}><TextField fullWidth size="small" type="date" label="Paid from" InputLabelProps={{ shrink: true }} value={draftFilters.paymentDateFrom} onChange={(e) => setDraftFilters((old) => ({ ...old, paymentDateFrom: e.target.value }))} /></Grid>
                      <Grid item xs={6} md={2}><TextField fullWidth size="small" type="date" label="Paid to" InputLabelProps={{ shrink: true }} value={draftFilters.paymentDateTo} onChange={(e) => setDraftFilters((old) => ({ ...old, paymentDateTo: e.target.value }))} /></Grid>
                      <Grid item xs={6} md={2}><TextField fullWidth size="small" label="Transaction reference" value={draftFilters.transactionReference} onChange={(e) => setDraftFilters((old) => ({ ...old, transactionReference: e.target.value }))} /></Grid>
                      <Grid item xs={6} md={2}><TextField fullWidth size="small" label="Payment reference" value={draftFilters.paymentReference} onChange={(e) => setDraftFilters((old) => ({ ...old, paymentReference: e.target.value }))} /></Grid>
                    </Grid>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5} sx={{ mt: 2.5 }}>
                      <Stack direction="row" spacing={1}><Button variant="contained" sx={primaryButtonSx} onClick={() => { setPage(1); setFilters(draftFilters); }}>Apply filters</Button><Button variant="text" color="error" onClick={() => { setDraftFilters(EMPTY_FILTERS); setFilters(EMPTY_FILTERS); setPage(1); }}>Clear filters</Button></Stack>
                      <TextField select size="small" label="Sort" value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }} sx={{ minWidth: 200 }}><MenuItem value="saleDate:desc">Newest sale first</MenuItem><MenuItem value="saleDate:asc">Oldest sale first</MenuItem><MenuItem value="suggestedPaymentDate:asc">Suggested date first</MenuItem><MenuItem value="netSellerAmount:desc">Highest net amount</MenuItem></TextField>
                    </Stack>
                  </CardContent>
                </Card>

                <Card sx={{ ...cardSx, width: "100%", minWidth: 0, overflow: "hidden" }}>
                  {paymentsError ? <Alert severity="error" action={<Button onClick={fetchPayments}>Retry</Button>}>{paymentsError}</Alert> : null}
                  <Box className="custom-scroll" sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflowX: "auto", overflowY: "hidden", overscrollBehaviorX: "contain" }}>
                    <Box component="table" sx={{ width: "100%", minWidth: 1660, borderCollapse: "separate", borderSpacing: 0, "th, td": { px: 1.75, py: 1.55, borderBottom: "1px solid #e9e9e9", textAlign: "left", fontSize: 13, verticalAlign: "middle" }, th: { bgcolor: "#171717", color: "white", fontWeight: 800, letterSpacing: ".015em", position: "sticky", top: 0, zIndex: 1, whiteSpace: "nowrap" }, "td:nth-of-type(5), td:nth-of-type(7), td:nth-of-type(8)": { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }, tbody: { "tr": { transition: "background-color .16s ease" }, "tr:hover": { bgcolor: "rgba(230,14,16,.04)", cursor: "pointer" } } }}>
                      <thead><tr>{["Buyer", "Seller", "Listing", "Asset", "Gross", "Fee rate", "Fee", "Net seller", "Sale date", "Transfer date", "Suggested date", "Actual payment", "Payment reference", "Status", "Email sent", "Action"].map((header) => <th key={header}>{header}</th>)}</tr></thead>
                      <tbody>
                        {paymentsLoading ? Array.from({ length: 7 }).map((_, row) => <tr key={row}>{Array.from({ length: 16 }).map((__, col) => <td key={col}><Skeleton /></td>)}</tr>) : payments.items.length ? payments.items.map((payment) => {
                          const overdue = payment.status === "Pending" && new Date(payment.suggestedPaymentDate) < new Date();
                          return <tr key={payment._id} onClick={() => openDetail(payment._id)}><td>{person(payment.buyer)}</td><td>{person(payment.seller)}</td><td>{shortId(payment.listingId)}</td><td>{payment.assetSnapshot?.shares || 0} shares</td><td>{currency(payment.grossAmount, payment.currency)}</td><td>{payment.appliedFeeRatePercent}%</td><td>{currency(payment.marketplaceFeeAmount, payment.currency)}</td><td><strong>{currency(payment.netSellerAmount, payment.currency)}</strong></td><td>{dateTime(payment.saleDate)}</td><td>{dateTime(payment.assetTransferDate)}</td><td>{dateTime(payment.suggestedPaymentDate)}</td><td>{dateTime(payment.actualPaymentDate)}</td><td>{payment.paymentReference || "—"}</td><td><StatusChip status={payment.status} overdue={overdue} /></td><td><EmailStatusChip payment={payment} /></td><td><Button size="small" color="error" onClick={(e) => { e.stopPropagation(); openDetail(payment._id); }}>View</Button></td></tr>;
                        }) : <tr><td colSpan={16}><Box sx={{ py: 8, textAlign: "center" }}><ReceiptLongOutlined sx={{ fontSize: 44, color: "#aaa" }} /><Typography sx={{ fontWeight: 800, mt: 1 }}>No seller payments found</Typography><Typography variant="body2" color="text.secondary">Completed transfers will appear here automatically.</Typography></Box></td></tr>}
                      </tbody>
                    </Box>
                  </Box>
                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">{payments.total || 0} payment records</Typography><Pagination page={page} count={Math.max(payments.totalPages || 1, 1)} onChange={(_, value) => setPage(value)} color="error" /></Stack>
                </Card>
              </Stack>
            ) : null}

            {tab === 2 ? (
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <Card sx={cardSx}><CardContent sx={{ p: 3 }}>
                    {feeLoading ? <Skeleton variant="rounded" height={240} /> : <><Stack direction="row" justifyContent="space-between"><Box><Typography variant="overline" color="text.secondary">Active marketplace fee</Typography><Typography variant="h2" sx={{ fontWeight: 900, color: "#E60E10", my: 1 }}>{Number(fee?.ratePercent || 0).toLocaleString()}%</Typography></Box><SettingsOutlined sx={{ color: "#E60E10" }} /></Stack><Alert severity="info" sx={{ my: 2 }}>Changes apply only to future marketplace payment orders. Existing and historical financial snapshots are never recalculated.</Alert><Info label="Last updated" value={dateTime(fee?.updatedAt)} /><Box sx={{ mt: 1.5 }}><Info label="Updated by" value={fee?.updatedBySnapshot?.name || person(fee?.updatedBy)} /></Box>{canManage ? <Button fullWidth variant="contained" sx={{ ...primaryButtonSx, mt: 3 }} onClick={() => { setFeeForm({ ratePercent: String(fee?.ratePercent ?? ""), reason: "" }); setFeeDialog(true); }}>Change future fee</Button> : null}</>}
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} md={7}>
                  <Card sx={cardSx}><CardContent sx={{ p: 3 }}><Typography variant="h6" sx={{ fontWeight: 850 }}>Fee change history</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Permanent record of configuration changes.</Typography><Stack spacing={1.25}>{feeHistory.length ? feeHistory.map((item) => <Box key={item._id} sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 2.5 }}><Stack direction="row" justifyContent="space-between" gap={2}><Typography sx={{ fontWeight: 850 }}>{item.previousRatePercent}% → <Box component="span" sx={{ color: "#E60E10" }}>{item.newRatePercent}%</Box></Typography><Typography variant="caption" color="text.secondary">{dateTime(item.changedAt)}</Typography></Stack><Typography variant="body2" color="text.secondary">{item.adminSnapshot?.name || item.adminSnapshot?.email || "Administrator"}{item.reason ? ` · ${item.reason}` : ""}</Typography></Box>) : <Typography variant="body2" color="text.secondary">No fee changes have been recorded.</Typography>}</Stack></CardContent></Card>
                </Grid>
              </Grid>
            ) : null}

            {tab === 3 ? (
              <Card sx={cardSx}><CardContent sx={{ p: 3 }}><Typography variant="h6" sx={{ fontWeight: 850 }}>Marketplace audit history</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Immutable seller-payment and fee-setting events.</Typography>{auditLoading ? <Stack spacing={1}>{Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} variant="rounded" height={72} />)}</Stack> : <Stack spacing={1.25}>{audit.length ? audit.map((item) => <Box key={`${item.category}-${item._id}`} sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 2.5, borderLeft: `4px solid ${item.category === "fee" ? "#E60E10" : "#f59e0b"}` }}><Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}><Box><Typography sx={{ fontWeight: 850 }}>{item.category === "fee" ? `Marketplace fee ${item.previousRatePercent}% → ${item.newRatePercent}%` : `${(item.actionType || "payment event").replaceAll("_", " ")}: ${item.previousStatus || "Created"} → ${item.newStatus || item.previousStatus || "Pending"}`}</Typography><Typography variant="body2" color="text.secondary">{item.adminSnapshot?.name || item.adminSnapshot?.email || "System"}{item.adminNotes || item.reason ? ` · ${item.adminNotes || item.reason}` : ""}</Typography></Box><Typography variant="caption" color="text.secondary">{dateTime(item.timestamp)}</Typography></Stack></Box>) : <Typography variant="body2" color="text.secondary">No audit events have been recorded.</Typography>}</Stack>}</CardContent></Card>
            ) : null}
          </Container>
        </Box>
      </Box>

      <Drawer anchor="right" open={Boolean(detail)} onClose={() => setDetail(null)} PaperProps={{ sx: { width: { xs: "100%", sm: 560 }, p: 0 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2.5, bgcolor: "#171717", color: "white", borderBottom: "3px solid #E60E10" }}><Box><Typography variant="h6" sx={{ fontWeight: 850 }}>Seller payment</Typography><Typography variant="caption" sx={{ opacity: .75 }}>{shortId(detail?._id)}</Typography></Box><IconButton onClick={() => setDetail(null)} sx={{ color: "white" }}><Close /></IconButton></Stack>
        {detailLoading ? <Box sx={{ p: 3 }}><Stack spacing={2}>{Array.from({ length: 8 }).map((_, index) => <Skeleton key={index} variant="rounded" height={50} />)}</Stack></Box> : detail ? <Box sx={{ p: 3, overflowY: "auto" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center"><StatusChip status={detail.status} overdue={detail.status === "Pending" && new Date(detail.suggestedPaymentDate) < new Date()} /><Typography variant="caption" color="text.secondary">Version {detail.__v}</Typography></Stack>
          <Card sx={{ mt: 2.5, borderRadius: 3, color: "white", background: "linear-gradient(135deg,#171717,#333)" }}><CardContent sx={{ p: 2.75 }}><Typography variant="caption" sx={{ opacity: .7 }}>NET SELLER AMOUNT</Typography><Typography variant="h4" sx={{ fontWeight: 900, my: .5, fontVariantNumeric: "tabular-nums" }}>{currency(detail.netSellerAmount, detail.currency)}</Typography><Typography variant="body2" sx={{ opacity: .8 }}>Gross {currency(detail.grossAmount, detail.currency)} · Fee {detail.appliedFeeRatePercent}% ({currency(detail.marketplaceFeeAmount, detail.currency)})</Typography></CardContent></Card>
          <Typography variant="subtitle1" sx={{ fontWeight: 850, mt: 3, mb: 1.5 }}>Parties & asset</Typography><Grid container spacing={2}><Grid item xs={6}><Info label="Seller" value={`${person(detail.sellerId)}${detail.sellerId?.email ? ` · ${detail.sellerId.email}` : ""}`} /></Grid><Grid item xs={6}><Info label="Buyer" value={`${person(detail.buyerId)}${detail.buyerId?.email ? ` · ${detail.buyerId.email}` : ""}`} /></Grid><Grid item xs={6}><Info label="Listing" value={String(detail.listingId?._id || detail.listingId)} /></Grid><Grid item xs={6}><Info label="Asset" value={`${detail.assetSnapshot?.shares || 0} shares @ ${currency(detail.assetSnapshot?.pricePerShare, detail.currency)}`} /></Grid><Grid item xs={6}><Info label="Offer" value={detail.offerId ? `${shortId(detail.offerId?._id || detail.offerId)} · ${detail.offerId?.status || ""}` : "Direct purchase"} /></Grid><Grid item xs={6}><Info label="Transaction" value={detail.transactionReference || shortId(detail.marketplaceOrderId?._id)} /></Grid></Grid>
          <Divider sx={{ my: 2.75 }} /><Typography variant="subtitle1" sx={{ fontWeight: 850, mb: 1.75 }}>Dates & payment</Typography><Grid container spacing={2.25}><Grid item xs={6}><Info label="Sale date" value={dateTime(detail.saleDate)} /></Grid><Grid item xs={6}><Info label="Asset transfer" value={dateTime(detail.assetTransferDate)} /></Grid><Grid item xs={6}><Info label="Suggested payment" value={dateTime(detail.suggestedPaymentDate)} /></Grid><Grid item xs={6}><Info label="Actual payment" value={dateTime(detail.actualPaymentDate)} /></Grid><Grid item xs={12}><Info label="Payment reference" value={detail.paymentReference || "—"} /></Grid><Grid item xs={12}><Info label="Admin notes" value={detail.adminNotes || "No notes"} /></Grid></Grid>
          <Divider sx={{ my: 2.75 }} />
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.75 }}><Typography variant="subtitle1" sx={{ fontWeight: 850 }}>Payment email</Typography><EmailStatusChip payment={detail} /></Stack>
          <Grid container spacing={2.25}><Grid item xs={12} sm={6}><Info label="Recipient" value={detail.paymentEmailRecipient || "Not recorded"} /></Grid><Grid item xs={12} sm={6}><Info label="Sent at" value={dateTime(detail.paymentEmailSentAt)} /></Grid><Grid item xs={12} sm={6}><Info label="Attempts" value={Number(detail.paymentEmailAttempts || 0)} /></Grid>{detail.paymentEmailError ? <Grid item xs={12}><Alert severity="warning">{detail.paymentEmailError}</Alert></Grid> : null}</Grid>
          {canManage ? <Stack direction="row" useFlexGap flexWrap="wrap" gap={1.25} sx={{ my: 3 }}><Button size="small" variant="contained" color="success" disabled={detail.status === "Paid"} onClick={() => beginAction("Paid")}>Mark Paid</Button><Button size="small" variant="outlined" color="warning" disabled={detail.status === "Pending"} onClick={() => beginAction("Pending")}>Mark Pending</Button><Button size="small" variant="outlined" color="error" disabled={detail.status === "Cancelled"} onClick={() => beginAction("Cancelled")}>Cancel payment</Button><Button size="small" variant="text" color="error" onClick={() => beginAction("notes")}>Update notes</Button>{detail.status === "Paid" && !detail.paymentEmailSent ? <Button size="small" variant="contained" sx={primaryButtonSx} startIcon={<EmailOutlined />} onClick={() => setRetryConfirm(true)}>Retry Email</Button> : null}</Stack> : null}
          <Typography variant="subtitle1" sx={{ fontWeight: 850, mb: 1.5 }}>Audit timeline</Typography><Stack spacing={1.25}>{detail.auditHistory?.length ? detail.auditHistory.map((entry) => <Box key={entry._id} sx={{ pl: 2, py: .5, borderLeft: `3px solid ${entry.actionType?.includes("email") ? "#E60E10" : "#e5e7eb"}` }}><Typography variant="body2" sx={{ fontWeight: 800, textTransform: "capitalize" }}>{entry.actionType?.replaceAll("_", " ")}</Typography><Typography variant="caption" color="text.secondary">{entry.previousStatus || "Created"} → {entry.newStatus || entry.previousStatus} · {dateTime(entry.actionAt)}</Typography>{entry.adminNotes ? <Typography variant="body2">{entry.adminNotes}</Typography> : null}{entry.emailRecipient ? <Typography variant="body2">Recipient: {entry.emailRecipient}</Typography> : null}{entry.emailError ? <Typography variant="body2" color="error">{entry.emailError}</Typography> : null}<Typography variant="caption" color="text.secondary">{entry.adminSnapshot?.name || entry.adminSnapshot?.email || "System"}{entry.emailTrigger ? ` · ${entry.emailTrigger} triggered` : ""}</Typography></Box>) : <Typography variant="body2" color="text.secondary">No audit events found.</Typography>}</Stack>
        </Box> : null}
      </Drawer>

      <Dialog open={Boolean(action)} onClose={saving ? undefined : () => setAction(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 850 }}>{action === "notes" ? "Update admin notes" : `Mark seller payment ${action}`}</DialogTitle>
        <DialogContent sx={filterFieldSx}><Alert severity={action === "Cancelled" ? "warning" : "success"} sx={{ mb: 2 }}>{action === "Paid" ? <>Confirm manual payment of <strong>{currency(detail?.netSellerAmount, detail?.currency)}</strong> to {person(detail?.sellerId)}. A confirmation email will be sent after the payment is saved.</> : action === "Cancelled" ? <>This cancels the tracked payment to <strong>{person(detail?.sellerId)}</strong>. It does not reverse the completed share transfer.</> : action === "Pending" ? "Actual payment date and reference will be cleared and preserved in audit history." : "Notes changes are recorded in audit history."}</Alert>{action === "Paid" ? <Stack spacing={2}><TextField required type="datetime-local" label="Actual payment date" InputLabelProps={{ shrink: true }} value={actionForm.actualPaymentDate} onChange={(e) => setActionForm((old) => ({ ...old, actualPaymentDate: e.target.value }))} /><TextField required label="Payment reference" value={actionForm.paymentReference} onChange={(e) => setActionForm((old) => ({ ...old, paymentReference: e.target.value }))} /></Stack> : null}<TextField fullWidth multiline minRows={3} required={action === "Cancelled"} label={action === "Cancelled" ? "Cancellation reason" : "Admin notes"} value={actionForm.adminNotes} onChange={(e) => setActionForm((old) => ({ ...old, adminNotes: e.target.value }))} sx={{ mt: 2 }} />{action === "Cancelled" ? <FormControlLabel sx={{ mt: 1 }} control={<Checkbox checked={cancelConfirmed} onChange={(e) => setCancelConfirmed(e.target.checked)} />} label="I confirm this seller payment should be cancelled." /> : null}</DialogContent>
        <DialogActions><Button onClick={() => setAction(null)} disabled={saving}>Back</Button><Button variant="contained" color={action === "Cancelled" ? "error" : action === "Paid" ? "success" : undefined} sx={action === "Cancelled" || action === "Paid" ? undefined : primaryButtonSx} onClick={submitPaymentAction} disabled={saving || (action === "Cancelled" && !cancelConfirmed)}>{saving ? <CircularProgress size={22} color="inherit" /> : "Confirm"}</Button></DialogActions>
      </Dialog>

      <Dialog open={retryConfirm} onClose={retryingEmail ? undefined : () => setRetryConfirm(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 850 }}>Retry payment email?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>This will make one new delivery attempt for the Paid transaction. It will not change the payment status or financial values.</Alert>
          <Info label="Seller" value={person(detail?.sellerId)} />
          <Box sx={{ mt: 1.5 }}><Info label="Recipient" value={detail?.paymentEmailRecipient || detail?.sellerId?.email || "Seller account email"} /></Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setRetryConfirm(false)} disabled={retryingEmail}>Back</Button><Button variant="contained" sx={primaryButtonSx} onClick={retryPaymentEmail} disabled={retryingEmail}>{retryingEmail ? <CircularProgress size={22} color="inherit" /> : "Retry Email"}</Button></DialogActions>
      </Dialog>

      <Dialog open={feeDialog} onClose={saving ? undefined : () => setFeeDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 850 }}>Confirm marketplace fee change</DialogTitle><DialogContent><Alert severity="warning" sx={{ mb: 2 }}>The fee changes from <strong>{fee?.ratePercent || 0}%</strong> to <strong>{feeForm.ratePercent || 0}%</strong> for future marketplace payment orders only. Historical sales stay unchanged.</Alert><TextField fullWidth required type="number" label="New fee percentage" inputProps={{ min: 0, max: 100, step: .0001 }} value={feeForm.ratePercent} onChange={(e) => setFeeForm((old) => ({ ...old, ratePercent: e.target.value }))} /><TextField fullWidth multiline minRows={3} label="Reason or notes (optional)" value={feeForm.reason} onChange={(e) => setFeeForm((old) => ({ ...old, reason: e.target.value }))} sx={{ mt: 2 }} /></DialogContent><DialogActions><Button onClick={() => setFeeDialog(false)} disabled={saving}>Back</Button><Button variant="contained" sx={primaryButtonSx} onClick={submitFee} disabled={saving}>{saving ? <CircularProgress size={22} color="inherit" /> : "Apply to future sales"}</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarketplaceManagement;
